import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { AccessComponentEnum, AccessLevelEnum, User } from 'graphql-common';
import WhiteBox from '@lib/components/WhiteBox/WhiteBox';
import DashboardTitleHeader from '@lib/layouts/DashboardLayout/DashboardTitleHeader/DashboardTitleHeader';
import Button, { ButtonTypes } from '@lib/components/Button/Button';
import ListEmptyState from '@lib/components/ListEmptyState/ListEmptyState';
import PageOutOfRangeEmptyState from '@lib/components/PageOutOfRangeEmptyState/PageOutOfRangeEmptyState';
import CircleLoader from '@lib/components/CircleLoader/CircleLoader';
import ErrorPage from '@lib/components/ErrorPage/ErrorPage';
import getStatusFromError from '@lib/utils/getStatusFromError';
import getDescriptionFromError from '@lib/utils/getDescriptionFromError';
import Search from '@lib/components/Search/Search';
import Table from '@lib/components/Table/Table';
import useSearchParam from '@lib/hooks/useSearchParam';
import { useTableItemsBulkSelect } from '@lib/hooks/useTableItemsBulkSelect';
import useModalManagement, { Modals } from '@lib/hooks/useModalManagement';
import useUsersActions from 'routes/Users/utils/useUsersActions';
import UserModalForm from 'components/UserModalForm/UserModalForm';
import ViewOnlyChip from '@lib/components/ViewOnlyChip/ViewOnlyChip';
import useOutletContext, { ContextProps } from 'hooks/useOutletContext';
import { APP_URLS } from 'constants/urls';
import getDepartmentMembersTableColumns from '../utils/getDepartmentMembersTableColumns';
import getDepartmentMembersTableActionsMenu from '../utils/getDepartmentMembersTableActionsMenu';
import { UseDepartmentQueriesResult } from '../utils/useDepartmentQueries';

const modals = [Modals.UserFormModal];

type Props = {
  useDepartmentQueriesResult: UseDepartmentQueriesResult;
};

export default function DepartmentViewMembers({
  useDepartmentQueriesResult,
}: Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id = '' } = useParams();
  const [searchQuery] = useSearchParam();
  const useBulkSelectResult = useTableItemsBulkSelect<User>();
  const useModalManagementResult = useModalManagement(modals);
  const { isModalOpened, closeModal } = useModalManagementResult;

  const { useDepartmentQueryHookResult, useDepartmentMembersQueryHookResult } =
    useDepartmentQueriesResult;
  const { data } = useDepartmentQueryHookResult;
  const { name } = data || {};

  const { getUserAccessByComponent }: ContextProps = useOutletContext();
  const isViewOnly =
    getUserAccessByComponent(AccessComponentEnum.UsersManagement) ===
    AccessLevelEnum.Read;
  const isDepartmentViewOnly =
    getUserAccessByComponent(AccessComponentEnum.DepartmentsManagement) ===
    AccessLevelEnum.Read;

  const {
    currentUserId,
    onActivate,
    onDeactivate,
    onEdit,
    onEditAccesses,
    onResendEmailConfirmationHandler,
    onResendRegistrationConfirmationHandler,
    onSubmitUserFormCallback,
    openUserViewPage,
    selectedUserId,
  } = useUsersActions({
    useUsersQueryHookResult: useDepartmentMembersQueryHookResult,
    useBulkSelectResult,
    useModalManagementResult,
  });

  const { collection, loading, firstLoading, error, totalCount } =
    useDepartmentMembersQueryHookResult;

  const onEditDepartment = () =>
    navigate(
      APP_URLS.dashboard.departments.edit.getDynamicPath({
        pathParts: { id },
      }),
    );

  const tableColumns = getDepartmentMembersTableColumns({
    t,
    onResendEmailConfirmationHandler,
    onResendRegistrationConfirmationHandler,
    isViewOnly,
  });
  const tableActionsMenu = getDepartmentMembersTableActionsMenu({
    currentUserId,
    isViewOnly,
    onActivate,
    onDeactivate,
    onEdit,
    onEditAccesses,
    onView: openUserViewPage,
    t,
  });

  const isEmptyStateVisible = collection.length === 0 && !error && !loading;
  const isEmptyStateByOutOfRangePage = isEmptyStateVisible && !!totalCount;
  let emptyState: React.ReactNode = null;
  if (isEmptyStateByOutOfRangePage) {
    emptyState = <PageOutOfRangeEmptyState totalEntries={totalCount} />;
  } else if (isEmptyStateVisible) {
    emptyState = (
      <ListEmptyState
        title={t('no-department-members-linked-yet')}
        text={t('no-department-members-linked-text')}
        actions={
          isDepartmentViewOnly
            ? []
            : [
                <Button
                  key="edit"
                  buttonText={t('edit-department')}
                  buttonType={ButtonTypes.primaryFilled}
                  leftIcon="edit"
                  onClick={onEditDepartment}
                  disabled={loading}
                />,
              ]
        }
      />
    );
  }

  const isErrorStateVisible = !!error && !loading;
  const errorState = isErrorStateVisible ? (
    <ErrorPage
      status={getStatusFromError(error)}
      description={getDescriptionFromError(error)}
    />
  ) : null;

  const isContentVisible = !error && !firstLoading && collection?.length;
  const content = isContentVisible ? (
    <>
      {(!!collection?.length || searchQuery) && (
        <Search placeholder={t('member-search-placeholder')} />
      )}
      <Table
        actionsMenu={tableActionsMenu}
        columns={tableColumns}
        data={collection}
        emptyState={emptyState}
        id="departmentMembers"
        loading={loading}
        totalEntries={totalCount}
        useBulkSelectResult={useBulkSelectResult}
      />
    </>
  ) : null;

  return (
    <>
      <WhiteBox>
        <DashboardTitleHeader
          title={name}
          actions={
            isDepartmentViewOnly ? (
              <ViewOnlyChip key="view" />
            ) : (
              <Button
                key="edit"
                buttonText={t('edit')}
                buttonType={ButtonTypes.primaryOutlined}
                leftIcon="edit"
                onClick={onEditDepartment}
              />
            )
          }
        />
        {firstLoading && <CircleLoader />}
        {errorState}
        {emptyState}
        {content}
      </WhiteBox>
      <UserModalForm
        isOpen={isModalOpened(Modals.UserFormModal)}
        onClose={() => closeModal(Modals.UserFormModal)}
        onSubmitUserFormCallback={onSubmitUserFormCallback}
        selectedUserId={selectedUserId}
        title={
          selectedUserId
            ? t('edit-department-member')
            : t('add-department-member')
        }
      />
    </>
  );
}
